import * as React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql } from 'gatsby';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GatsbySite } from '../../graphql';
import SEO from '../../components/seo';
import Section from '../../components/project/section';
import { CenteredContent } from '../../components/utility/section';
import { SpacingWrapper } from '../../components/utility/spacing-wrapper';
import TitlePage from '../../components/project/TitlePage';
import Client from '../../components/project/client';
import ResponsiveParagraph from '../../components/utility/ResponsiveParagraph';
import ResponsiveDiv from '../../components/utility/ResponsiveDiv';
import { SectionTag } from '../../components/project/section-tag';
import Quote from '../../components/project/quote';
import Tech from '../../components/project/tech';
import Carousel from '../../components/project/carousel';
import NextMissions from '../../components/project/next-missions';
// import photos
import logo from '../../images/projects/otc/logo.png';
import beautifullySimple from '../../images/projects/otc/beautifully-simple.png';
import backgroundImage from '../../images/projects/otc/background-image.png';
import webExperience from '../../images/projects/otc/web-experience.png';
import alberio from '../../images/projects/employee-avatars/alberio-bathory-frota.png';
import threePhones from '../../images/projects/otc/three-phones.png';
import background from '../../images/projects/enjoyn/background.png';
import reactLogo from '../../images/projects/otc/react-logo.png';
import carousel1 from '../../images/projects/otc/carousel1.png';
import carousel2 from '../../images/projects/otc/carousel2.png';
import carousel3 from '../../images/projects/otc/summary.png';
import phonePicture from '../../images/projects/otc/phone-picture.png';
import watches from '../../images/projects/otc/watches.png';

const OTCPage = () => {
  const { site } = useStaticQuery<GatsbySite>(
    graphql`
      query {
        site {
          siteMetadata {
            projects {
              name
              description
              imageName
              link
            }
          }
        }
      }
    `,
  );

  const { siteMetadata } = site;

  const projects = siteMetadata?.projects || [];
  return (
    <>
      <SEO title="Olympia Trust Company" />
      <div id="#top" />
      <TitlePage
        title="Olympia: A financial services app development project"
        content="All the information clients want about their investment accounts, at their fingertips. Easy Peasy."
        backgroundImage={background}
      />
      <SpacingWrapper>
        <Client
          content={`Olympia Trust Company (OTC) is a non-deposit taking trust company that provides self-directed investment accounts to Canadians. Based in Calgary, Alberta, the Olympia Financial Group subsidiary began operations in 1996, and also offers foreign exchange and other trustee services to its large customer base.`}
          images={[{ image: logo, url: 'https://www.olympiatrust.com/' }]}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={beautifullySimple}
              alt="Two iPhones displaying the beautiful simple design of the Olympia RRSP app"
              style={{ maxWidth: 498, width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`In 2020, Canadians opened 2.3 million self-directed investment accounts, three times the number in 2019¹. The number for these DIY accounts is only expected to grow in the post-pandemic world as investors take matters into their own hands.  
          The industry is also growing at a time when technology is making banking and finance-related services ever more accessible, whether it is online banking or getting a mortgage. `}
          p2={`Olympia wanted to make it easier for its clients to access their accounts, make online transactions and payments, among other things. 
          The OTC Online portal and app did exactly that and much more. With secure online infrastructure, integration with payment solutions, intuitive UX/UI and a range of convenient features, the solution provided another efficient and functional touchpoint between clients and the company. `}
          heading="The bird's eye view"
          subHeading="summary"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={webExperience}
              alt="A laptop displaying the web experience"
              style={{ width: '100%', maxWidth: 1040 }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`According to an April 2021 survey of self-directed investors by Ontario Securities Commission, a quarter of the respondents were below the age of 34². 
          As many as 25 percent of the respondents signed into their online accounts daily, while 28 percent did so weekly³. Aren't we all curious about our money? 
          According to another 2020 study, 46 percent of self-directed investors expressed dissatisfaction with their investment firm's web portals⁴.`}
          p2={`Referral rates for investment firms among satisfied investors is as high as 59 percent⁵, according to some estimates. As the average age of investors decreases, technological touchpoints are only expected to boost these metrics. 
          Olympia Trust Company had good reasons to seek an online portal and a mobile app to simplify the experience for its large customer base.`}
          heading="Taking Olympia online and into the future"
          subHeading="pain"
          ignoreCase
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <SimpleUXImageWrapper>
          <img
            src={backgroundImage}
            alt="Several iphones displaying the app"
            style={{ width: '100%' }}
          />
        </SimpleUXImageWrapper>
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>vision</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Mobile and web app development</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              OTC wanted a professional, user-friendly, client-facing web portal and a corresponding
              mobile app to make it simpler for clients to access account-specific information, and
              undertake some of the more straightforward tasks and transactions. The portal and the
              app had to integrate well with OTC’s proprietary backend as part of a secure
              infrastructure.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="They wanted a company that came in, understood their business, and built them a solution. The approach was very different from the big builds, especially in the financial industry. We came in with the mindsets of their clients, figured what was important to them, and built from there."
          citation="Alberio Bathory-Frota"
          citation2="CEO @ Launchcode"
          picture={alberio}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={threePhones}
              alt="Three phones displaying different versions of the total assets screen"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <Tech
          content={`Given the regulatory requirements and OTC's desire to handle the data internally, the backend was handled by their IT development team. It also meant that Launchcode had to frequently coordinate with the team for APIs, testing, etc. 
          The mobile application for the project was built in React Native. This helped when further complimenting the app with an online portal, which was built in React. 
          With the solutions also integrating payment capabilities, it was one well-rounded all-powerful application that worked across demographics and diverse technological capabilities. `}
          images={[reactLogo]}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Carousel
          images={[
            { image: carousel1, alt: 'A graph of total accounts opened' },
            { image: carousel2, alt: 'Web view of accounts' },
            { image: carousel3, alt: 'Personalized investment summary' },
          ]}
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`Early Spring of 2018, Launchcode wowed OTC decision-makers with an impressive mock-up as part of a proposal. Clean interface, convenient features and a robust understanding of the task at hand won it for Launchcode. 
          Follow-up involved understanding OTC's proprietary backend, their already existing app, its capabilities, dependencies on the client's in-house IT team, and the goals behind the whole project. 
          As a development company, Launchcode focuses on accomplishing the client's vision. That holistic process involves understanding what's important to the client and to their customer base.  
          The first step involved a deep dive into the business model, the services offered, the data the company held, etc. 
          The user journey for the mobile app had to ensure customers had access to numbers they were looking for as intuitively and as quickly as possible. `}
          p2={`In the already existing app, calculations for a lot of these numbers were done at every instance of showing, involving the system reaching out to the backend at each instance, making the process much slower. While the process would have still worked for a single client, it would only overload the system to repeat the process for thousands of clients, and brokers with many more clients in real time. 
          Launchcode developers incorporated a cache system at the frontend to make the process faster. 
          While users interacted with the app, the solution reached out to the backend again for the most commonly asked-for transactions to have the information ready. 
          Payment integration made it possible for customers to add funds to their accounts, pay fees, among other things, cutting down a lot of paperwork on OTC's part. 
          New user registration, dashboards, digital signatures, documentation, customer service, made it easier for customers to invest through OTC. 
          Launchcode developers added functionalities that empowered customers to make their own investments, open new accounts, make changes, fill up forms, etc. 
          Multiple sprints turned into phases, and newer features found their way to the web portal and the app.`}
          heading="Financial services app development"
          subHeading="journey"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <MultipleDeviceImageWrapper>
              <img src={phonePicture} alt="Enjoyn's waiting room screen displayed on an Iphone" />
            </MultipleDeviceImageWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <SectionTag>ux/ui</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>What you see is what you get</strong>
            </h2>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              For most out there, finance and financial information can be quite daunting. The least
              any organization in the financial industry can do is simplify these complex matters
              for its clients. This would mean that various touchpoints must be cohesive, work
              seamlessly, and be user-friendly for an audience of wide technological capabilities.
              <br />
              <br />
              Keeping that in mind, visual cues, including color coding and icons, were incorporated
              to identify the various accounts a user may have.
              <br />
              <br />
              Striking a balance between presenting information for experts and novices alike was
              key.
              <br />
              <br />
              Taking all the user experience over to the web portal, which lent users the benefit of
              larger screens in most cases, and more features and functionalities, was part of
              successive phases.
              <br />
              <br />
              Everything from brand identity to color schemes, and functionality had to be
              consistent between the app and the portal.
              <br />
              <br />
              Multiple interfaces were created and tested before one was chosen.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <Section
          p1={`After the positive response to the mock-ups at the proposal stage, it was important for Launchcode to build on those expectations. And that's indeed what happened. 
          The solution inspired operational efficiency within Olympia, also resulting in decreased amount of paperwork.`}
          p2={`With the payment integration in the app and the web portal, client deposits and fee payments exponentially increased. 
          The app has excellent ratings on both the Play Store and the App Store, with thousands of installs among the customers, indicating strong adoption. `}
          subHeading="results"
          heading="A financial security solution that is efficient and easy-to-use"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={watches}
              alt="Two smartwatches displaying the app"
              style={{ width: '100%', maxWidth: 724 }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>Response</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Clients love us!</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              Olympia Trust Company had dabbled with creating a solution internally but were some
              way off from what their vision was.
              <br />
              <br />
              In Launchcode, OTC appreciated a solution provider that was focused on understanding
              their business, what they need, what their customers want, work with their internal
              team, and create an easy-to-use high-performing app with a premium look and feel.
              <br />
              <br />
              An open communication through bi-weekly Sprints was appreciated as much as the
              performance of the solution.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <Quote
          quote="The quality of the work is exceptional. The software has created more efficiency internally, so we don’t have to process any paperwork. What they produced exceeded my expectations. They wanted us to be happy with the project, so they came to us with solutions that we needed."
          citation="Charlie Griffith"
          citation2="Head of Client Experience @ Olympia Trust Company"
        />
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>Next Steps</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>Innovation does not rest</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              Given that Launchcode developers did not have control over the entire build of the
              solution, the team did well to ensure transparent and open communication with the
              stakeholders.
              <br />
              <br />
              The relationship has grown from a project-based one to a continuous one that
              frequently sees collaboration on adding new features, besides maintenance and support.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>
      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <CitationsTitleWrapper>
              <h2
                style={{
                  fontWeight: 500,
                  fontSize: 48,
                  maxWidth: 730,
                  textAlign: 'left',
                }}
              >
                <strong>Extras</strong>
              </h2>
            </CitationsTitleWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <h3 style={{ fontWeight: 700, fontSize: 20 }}>Download the app from:</h3>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              <a
                href="https://apps.apple.com/ca/app/olympia-mobile/id1438916847"
                rel="nofollow noreferrer"
                target="_blank"
              >
                App Store
              </a>{' '}
              and{' '}
              <a
                href="https://play.google.com/store/apps/details?id=com.olympiatrust.rspmobile&hl=en_CA&gl=US"
                rel="nofollow noreferrer"
                target="_blank"
              >
                Play Store
              </a>
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <CitationsTitleWrapper>
            <h2
              style={{
                fontWeight: 500,
                fontSize: 48,
                maxWidth: 730,
                textAlign: 'left',
              }}
            >
              <strong>Citations</strong>
            </h2>
          </CitationsTitleWrapper>
        </ResponsiveDiv>

        <ResponsiveDiv>
          <ResponsiveParagraph>
            <p>
              <sup>1</sup>
              <a
                href="https://globalnews.ca/news/7631776/diy-investing-canada-iiroc/"
                target="_blank"
                rel="nofollow noreferrer"
              >
                Alini, Erica. "Canadians opened 2.3 million DIY investing accounts in 2020. Should
                you?"
              </a>{' '}
              Global News, 11 February 2021. Accessed 10 May 2022.{' '}
            </p>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>2 & 3</sup>
              <a
                href="https://www2.deloitte.com/content/dam/Deloitte/global/Documents/Risk/gx-risk-deloitte-global-itam-survey-results-2021.pdf."
                target="_blank"
                rel="nofollow noreferrer"
              >
                "Self-Directed Investors: Insights And Experiences".
              </a>{' '}
              Deloitte. Accessed 28 Jun 2022.{' '}
            </p>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>4 & 5</sup>
              <a
                href="https://www.jdpower.com/business/press-releases/2020-canada-self-directed-investor-satisfaction-study"
                target="_blank"
                rel="nofollow noreferrer"
              >
                "COVID-19 Exacerbates Client Experience Challenges for Canadian Self-Directed
                Investment Firms, J.D. Power Finds".
              </a>{' '}
              JD Power, 28 May 2020. Accessed 10 May 2022.{' '}
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <NextMissions currentProjectName="Olympia" projects={projects} />

      <CenteredContent style={{ marginBottom: 40 }}>
        <a href="#top">Back to top ⇮</a>
      </CenteredContent>
    </>
  );
};

const CitationsTitleWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: 40px;
  }

  @media screen and (min-width: 1250px) {
    margin-left: 40px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.wideScreen}) {
    margin-left: 40px;
  }
`;

const MultipleDeviceImageWrapper = styled.div`
  width: 100%;
  height: 100%;

  > div {
    transform: scale(1.075);
  }
`;

const SimpleUXImageWrapper = styled.div`
  > div {
    overflow: unset;
    max-width: 70ch;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      top: 12.5%;
    }
  }
`;

export default OTCPage;
